/**
 * What a traveler reads when the server failed.
 *
 * traveler-api already swaps every 5xx message for one generic sentence
 * (`sendErrorEnvelope`, packages/bootstrap/src/http/error-envelope.ts). This
 * side does not rely on that: a 5xx that reaches the storefront through a
 * proxy, an older API, or a route that forgot the envelope must not put its
 * text in front of a customer either. So the rule is decided here from the
 * status, and the server's message is shown only when the envelope says
 * `public: true` — which `serverErrorBody`
 * (packages/bootstrap/src/http/error-envelope.ts:80) sets only for a 5xx
 * carrying `publicMessage` fixed copy.
 *
 * operator-web has the same rule in its own `server-failure.ts`.
 */

import {
  readPublicMessage,
  readRequestId,
  shortReference,
} from "@tripcart/shared/envelope/request-id";

import type { Translator } from "@/lib/translation-context";

export interface FailedResponse {
  status: number;
  headers?: Headers | null;
}

// No `<= 0` as in operator-web: here a request that got no answer throws, and
// that goes through `humanForTransportError` instead.
export function isServerFailureStatus(status: number): boolean {
  return status >= 500;
}

/**
 * The server's own words, kept for whoever is reading the console.
 *
 * Called where a failed response is first read, never from
 * `serverFailureMessage` — a formatter runs once per render, and this needs
 * to run once per response.
 */
export function logServerFailure(
  body: unknown,
  response: FailedResponse,
): void {
  if (!isServerFailureStatus(response.status)) return;
  if (readPublicMessage(body) !== undefined) return;
  console.error("[api] server failure", { status: response.status, body });
}

/**
 * The line to show for a 5xx. Unless the envelope marked its message
 * `public`, the server's own words never reach the page. The reference is the
 * short form of the request id — support finds the request by searching the
 * logs' `req.id` for that prefix.
 *
 * Returns text and nothing else, so a component may call it during render.
 */
export function serverFailureMessage(
  body: unknown,
  response: FailedResponse,
  t: Translator,
): string {
  const shown = readPublicMessage(body);
  if (shown) return shown;
  const requestId = readRequestId(body, response.headers);
  return requestId
    ? t(
        "Something went wrong on our side. Please try again in a moment. (ref: {ref})",
        { ref: shortReference(requestId) },
      )
    : t("Something went wrong on our side. Please try again in a moment.");
}
